import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { isPamyatniki } from '../lib/variant';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Landmark, 
  LogOut, 
  Plus, 
  Pencil, 
  Trash2, 
  Layers, 
  Package,
  Home,
  Upload,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  Shapes,
  Gem
} from 'lucide-react';
import useStore from '../store/useStore';
import { formatDimensions } from '../lib/utils';
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getElements,
  createElement,
  updateElement,
  deleteElement,
  getStones,
  ensureDefaultStones,
  createStone,
  updateStone,
  deleteStone,
  uploadModel,
  uploadCategoryIcon,
  uploadTexture,
  BACKEND_BASE_URL,
  getModelTypes,
  updateModelType
} from '../api';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 10;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useStore();
  const [categories, setCategories] = useState([]);
  const [elements, setElements] = useState([]);
  const [stones, setStones] = useState([]);
  const [modelTypes, setModelTypes] = useState([]);
  const [editingModelType, setEditingModelType] = useState(null);
  const [modelTypeForm, setModelTypeForm] = useState({ name_ru: '' });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('categories');
  // Variant tab — splits catalog admin into "Благоустройство" / "Памятники".
  // Persisted so a refresh keeps the same context the admin was working in.
  const [activeVariant, setActiveVariant] = useState(
    () => localStorage.getItem('admin_variant') || 'blagoustroistvo'
  );
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Pagination states
  const [categoriesPage, setCategoriesPage] = useState(1);
  const [elementsPage, setElementsPage] = useState(1);
  
  // Category form
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    name_ru: '',
    description: '',
    icon_url: '',
    is_draggable: true,
    is_rotatable: true,
    order: 0,
    installation_price: 0
  });
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const categoryIconInputRef = React.useRef(null);

  // Element form
  const [elementDialog, setElementDialog] = useState(false);
  const [editingElement, setEditingElement] = useState(null);
  const [elementForm, setElementForm] = useState({
    category_id: '',
    name: '',
    name_ru: '',
    description: '',
    price: 0,
    price_unit: 'piece',
    stone_id: '',
    width: 1,
    height: 1,
    depth: 1,
    color: '',
    model_type: 'box',
    model_url: '',
    thumbnail_url: '',
    section_width: 0.5,
    is_full_fence: true,
    flowerbed_shape: 'p',
    is_flowerbed_filling: false,
    price_ratio: 100,
    installation_price: 0
  });
  const [uploadingModel, setUploadingModel] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const elementThumbnailInputRef = React.useRef(null);

  // Stone form (справочник камней, ₸/м³)
  const [stoneDialog, setStoneDialog] = useState(false);
  const [editingStone, setEditingStone] = useState(null);
  const [stoneForm, setStoneForm] = useState({
    name_ru: '',
    price_per_cubic_meter: 0,
    texture_url: '',
    thumbnail_url: '',
    is_active: true,
    order: 0
  });
  const [uploadingStoneThumbnail, setUploadingStoneThumbnail] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, activeVariant]);

  const loadData = async () => {
    try {
      // Categories/elements scoped to active variant. Stones and model types
      // are shared across both sites — no variant filter for those.
      const [catRes, elemRes, stonesRes, mtRes] = await Promise.all([
        getCategories(activeVariant),
        getElements(undefined, true, activeVariant),
        getStones(),
        getModelTypes()
      ]);
      setCategories(catRes.data);
      setElements(elemRes.data);
      setStones(stonesRes.data);
      setModelTypes(mtRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleVariantChange = (variant) => {
    if (variant === activeVariant) return;
    localStorage.setItem('admin_variant', variant);
    setActiveVariant(variant);
    // Old context is invalid in the new variant — close open dialogs, drop
    // category-id-based filters, reset pagination. loadData refires via the
    // activeVariant dep on the auth useEffect.
    setCategoryDialog(false);
    setElementDialog(false);
    setEditingCategory(null);
    setEditingElement(null);
    setCategoryFilter('all');
    setCategoriesPage(1);
    setElementsPage(1);
  };

  // Category handlers
  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        name_ru: category.name_ru,
        description: category.description || '',
        icon_url: category.icon_url || '',
        is_draggable: category.is_draggable,
        is_rotatable: category.is_rotatable,
        order: category.order,
        installation_price: category.installation_price || 0
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: '',
        name_ru: '',
        description: '',
        icon_url: '',
        is_draggable: true,
        is_rotatable: true,
        order: categories.length,
        installation_price: 0
      });
    }
    setCategoryDialog(true);
  };

  const handleCategoryIconFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingIcon(true);
    try {
      const res = await uploadCategoryIcon(file);
      const url = res.data?.url;
      if (url) {
        setCategoryForm(prev => ({ ...prev, icon_url: url }));
        toast.success('Иконка загружена');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка загрузки иконки');
    } finally {
      setUploadingIcon(false);
      e.target.value = '';
    }
  };

  const handleCategorySubmit = async () => {
    try {
      if (editingCategory) {
        // Don't override existing record's variant on edit — UpdateCategory
        // is partial, so omitting variant preserves it.
        await updateCategory(editingCategory.id, categoryForm);
        toast.success('Категория обновлена');
      } else {
        await createCategory({ ...categoryForm, variant: activeVariant });
        toast.success('Категория создана');
      }
      setCategoryDialog(false);
      loadData();
    } catch (error) {
      toast.error('Ошибка сохранения категории');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Удалить категорию и все её элементы?')) return;
    try {
      await deleteCategory(id);
      toast.success('Категория удалена');
      loadData();
    } catch (error) {
      toast.error('Ошибка удаления категории');
    }
  };

  // Element handlers
  const openElementDialog = (element = null) => {
    if (element) {
      setEditingElement(element);
      setElementForm({
        category_id: element.category_id,
        name: element.name,
        name_ru: element.name_ru,
        description: element.description || '',
        price: element.price,
        price_unit: element.price_unit || 'piece',
        stone_id: element.stone_id || '',
        width: element.width,
        height: element.height,
        depth: element.depth,
        color: element.color,
        model_type: element.model_type,
        model_url: element.model_url || '',
        thumbnail_url: element.thumbnail_url || '',
        section_width: element.section_width || 0.5,
        is_full_fence: element.is_full_fence !== false,
        flowerbed_shape: element.flowerbed_shape === 'rectangle' ? 'rectangle' : 'p',
        is_flowerbed_filling: element.is_flowerbed_filling || false,
        price_ratio: element.price_ratio ?? 100,
        installation_price: element.installation_price || 0
      });
    } else {
      setEditingElement(null);
      setElementForm({
        category_id: categoryFilter !== 'all' ? categoryFilter : (categories[0]?.id || ''),
        name: '',
        name_ru: '',
        description: '',
        price: 0,
        price_unit: 'piece',
        stone_id: '',
        width: 1,
        height: 1,
        depth: 1,
        color: '',
        model_type: 'box',
        model_url: '',
        thumbnail_url: '',
        section_width: 0.5,
        is_full_fence: true,
        flowerbed_shape: 'p',
        is_flowerbed_filling: false,
        installation_price: 0
      });
    }
    setElementDialog(true);
  };

  const handleModelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['glb', 'gltf'].includes(ext)) {
      toast.error('Поддерживаются только GLB и GLTF файлы');
      return;
    }
    
    setUploadingModel(true);
    try {
      const res = await uploadModel(file);
      setElementForm({ ...elementForm, model_url: res.data.url });
      toast.success('3D модель загружена');
    } catch (error) {
      toast.error('Ошибка загрузки модели');
    } finally {
      setUploadingModel(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingThumbnail(true);
    try {
      const res = await uploadCategoryIcon(file);
      const url = res.data?.url;
      if (url) {
        setElementForm(prev => ({ ...prev, thumbnail_url: url }));
        toast.success('Иконка загружена');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка загрузки иконки');
    } finally {
      setUploadingThumbnail(false);
      e.target.value = '';
    }
  };

  const handleElementSubmit = async () => {
    try {
      // flowerbed_shape осмыслен только для цветников. Для прочих категорий (тумба,
      // памятник, гравировка…) принудительно null — иначе элемент считается П-образным
      // и цена берётся по формуле рамки (0.08·h·(2d+w)) вместо объёма коробки w·h·d.
      const cat = categories.find(c => c.id === elementForm.category_id);
      const isFlowerbedCat = !!cat && ((cat.name || '').toLowerCase() === 'flowerbed' || (cat.name_ru || '').toLowerCase().includes('цветник'));
      const payload = {
        ...elementForm,
        price: Number(elementForm.price) || 0,
        installation_price: Number(elementForm.installation_price) || 0,
        flowerbed_shape: isFlowerbedCat ? (elementForm.flowerbed_shape || 'p') : null,
      };
      if (editingElement) {
        // Partial update — variant of existing record is preserved server-side.
        await updateElement(editingElement.id, payload);
        toast.success('Элемент обновлён');
      } else {
        await createElement({ ...payload, variant: activeVariant });
        toast.success('Элемент создан');
      }
      setElementDialog(false);
      loadData();
    } catch (error) {
      // Показываем понятную причину, чтобы было ясно, почему после обновления страницы всё "откатывается"
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 401) {
        toast.error('Сессия админа закончилась. Перезайдите и попробуйте ещё раз.');
      } else {
        toast.error(detail || 'Ошибка сохранения элемента');
      }
    }
  };

  const handleDeleteElement = async (id) => {
    if (!window.confirm('Удалить элемент?')) return;
    try {
      await deleteElement(id);
      toast.success('Элемент удалён');
      loadData();
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 401) {
        toast.error('Сессия истекла. Перезайдите в админку.');
      } else {
        toast.error(detail || 'Ошибка удаления элемента');
      }
    }
  };

  // Stone handlers (справочник камней для тумб, цветников, памятников)
  const openStoneDialog = (stone = null) => {
    if (stone) {
      setEditingStone(stone);
      setStoneForm({
        name_ru: stone.name_ru,
        price_per_cubic_meter: stone.price_per_cubic_meter,
        texture_url: stone.texture_url || '',
        thumbnail_url: stone.thumbnail_url || '',
        is_active: stone.is_active !== false,
        order: stone.order ?? 0
      });
    } else {
      setEditingStone(null);
      setStoneForm({ name_ru: '', price_per_cubic_meter: 0, texture_url: '', thumbnail_url: '', is_active: true, order: stones.length });
    }
    setStoneDialog(true);
  };

  const stoneImageSrc = (url) => {
    if (!url || typeof url !== 'string') return null;
    const u = url.trim();
    if (!u) return null;
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    const base = (BACKEND_BASE_URL || '').replace(/\/$/, '');
    return base ? base + (u.startsWith('/') ? u : '/' + u) : u;
  };

  const handleStoneThumbnailUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setUploadingStoneThumbnail(true);
    try {
      const res = await uploadTexture(file);
      const url = res.data?.url;
      if (url) {
        setStoneForm(prev => ({ ...prev, thumbnail_url: url }));
        toast.success('Картинка загружена. Нажмите «Сохранить», чтобы применить.');
      } else {
        toast.error('Сервер не вернул URL файла');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Ошибка загрузки';
      toast.error(msg);
    } finally {
      setUploadingStoneThumbnail(false);
      e.target.value = '';
    }
  };
  const handleStoneSubmit = async () => {
    try {
      const payload = {
        name_ru: String(stoneForm.name_ru ?? ''),
        price_per_cubic_meter: Number(stoneForm.price_per_cubic_meter) || 0,
        texture_url: stoneForm.texture_url ? String(stoneForm.texture_url) : null,
        thumbnail_url: stoneForm.thumbnail_url ? String(stoneForm.thumbnail_url) : null,
        is_active: Boolean(stoneForm.is_active),
        order: Number(stoneForm.order) ?? 0
      };
      if (editingStone) {
        const res = await updateStone(editingStone.id, payload);
        const updated = res?.data ? { ...editingStone, ...res.data } : { ...editingStone, ...payload };
        setStones(prev => prev.map(s => (s.id === editingStone.id ? updated : s)));
        toast.success('Камень обновлён');
      } else {
        const res = await createStone(payload);
        if (res?.data) setStones(prev => [...prev, res.data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
        toast.success('Камень добавлен');
      }
      setStoneDialog(false);
      // Не вызываем loadData() — список уже обновлён из ответа, иначе можно перезаписать свежие данные
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения камня');
    }
  };
  const handleDeleteStone = async (id) => {
    if (!window.confirm('Удалить камень из справочника?')) return;
    try {
      await deleteStone(id);
      toast.success('Камень удалён');
      loadData();
    } catch (error) {
      toast.error('Ошибка удаления камня');
    }
  };

  const [ensuringStones, setEnsuringStones] = useState(false);
  const handleEnsureDefaultStones = async () => {
    setEnsuringStones(true);
    try {
      const res = await ensureDefaultStones();
      setStones(res.data);
      toast.success('Стандартные камни созданы');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка загрузки камней');
    } finally {
      setEnsuringStones(false);
    }
  };

  const handleSaveModelType = async () => {
    if (!editingModelType) return;
    try {
      const res = await updateModelType(editingModelType.key, { name_ru: modelTypeForm.name_ru });
      setModelTypes(prev => prev.map(mt => mt.key === editingModelType.key ? res.data : mt));
      setEditingModelType(null);
      toast.success('Название типа обновлено');
    } catch (error) {
      toast.error('Ошибка обновления');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('kk-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name_ru || 'Неизвестно';
  };

  // Get category system name by ID
  const getCategorySystemName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || '';
  };

  // Check if current element is fence or border (by category or model_type)
  const isFenceOrBorderElement = () => {
    const categoryName = getCategorySystemName(elementForm.category_id);
    return elementForm.model_type === 'fence' ||
           elementForm.model_type === 'border' ||
           categoryName === 'fence' ||
           categoryName === 'border';
  };

  // Тумба, цветник, памятник — цена берётся из справочника камня (₸/м³), не из поля элемента
  const isStoneBasedCategory = (categoryId) => {
    const cat = categories.find((c) => c.id === categoryId);
    if (!cat) return false;
    const name = (cat.name || '').toLowerCase();
    if (name === 'pedestal' || name === 'flowerbed' || name === 'monument') return true;
    const ru = (cat.name_ru || '').toLowerCase();
    // Если в БД другое системное имя — ориентируемся на русское название категории
    if (ru.includes('тумб')) return true;
    if (ru.includes('цветник')) return true;
    if (ru.includes('памятник')) return true;
    return false;
  };

  // Filtered and paginated categories
  const filteredCategories = useMemo(() => {
    return categories.filter(cat => 
      cat.name_ru.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cat.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [categories, searchQuery]);

  const paginatedCategories = useMemo(() => {
    const start = (categoriesPage - 1) * ITEMS_PER_PAGE;
    return filteredCategories.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredCategories, categoriesPage]);

  const totalCategoriesPages = Math.ceil(filteredCategories.length / ITEMS_PER_PAGE);

  // Filtered and paginated elements
  const filteredElements = useMemo(() => {
    return elements.filter(el => {
      const matchesSearch = el.name_ru.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           el.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || el.category_id === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [elements, searchQuery, categoryFilter]);

  const paginatedElements = useMemo(() => {
    const start = (elementsPage - 1) * ITEMS_PER_PAGE;
    return filteredElements.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredElements, elementsPage]);

  const totalElementsPages = Math.ceil(filteredElements.length / ITEMS_PER_PAGE);

  // Reset page when search/filter changes
  useEffect(() => {
    setCategoriesPage(1);
    setElementsPage(1);
  }, [searchQuery, categoryFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-dashboard">
      {/* Sidebar - Fixed height with sticky footer */}
      <div className="w-64 bg-slate-900 text-slate-300 flex-shrink-0 h-screen sticky top-0 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
              <Landmark className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-display text-lg text-white">Админ-панель</h1>
              <p className="text-xs text-slate-500">iRitual Дизайнер</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 overflow-hidden">
          <div className="space-y-1">
            <Button 
              variant="ghost" 
              className={`w-full justify-start hover:text-white hover:bg-slate-800 ${activeTab === 'categories' ? 'bg-slate-800 text-white' : 'text-slate-300'}`}
              onClick={() => setActiveTab('categories')}
              data-testid="sidebar-categories-btn"
            >
              <Layers className="h-4 w-4 mr-3" />
              Категории
              <Badge variant="secondary" className="ml-auto">{categories.length}</Badge>
            </Button>
            <Button 
              variant="ghost" 
              className={`w-full justify-start hover:text-white hover:bg-slate-800 ${activeTab === 'elements' ? 'bg-slate-800 text-white' : 'text-slate-300'}`}
              onClick={() => setActiveTab('elements')}
              data-testid="sidebar-elements-btn"
            >
              <Package className="h-4 w-4 mr-3" />
              Элементы
              <Badge variant="secondary" className="ml-auto">{elements.length}</Badge>
            </Button>
            <Button 
              variant="ghost" 
              className={`w-full justify-start hover:text-white hover:bg-slate-800 ${activeTab === 'stones' ? 'bg-slate-800 text-white' : 'text-slate-300'}`}
              onClick={() => setActiveTab('stones')}
              data-testid="sidebar-stones-btn"
            >
              <Gem className="h-4 w-4 mr-3" />
              Камни
              <Badge variant="secondary" className="ml-auto">{stones.length}</Badge>
            </Button>
            <Button 
              variant="ghost" 
              className={`w-full justify-start hover:text-white hover:bg-slate-800 ${activeTab === 'model-types' ? 'bg-slate-800 text-white' : 'text-slate-300'}`}
              onClick={() => setActiveTab('model-types')}
              data-testid="sidebar-model-types-btn"
            >
              <Shapes className="h-4 w-4 mr-3" />
              Типы моделей
              <Badge variant="secondary" className="ml-auto">{modelTypes.length}</Badge>
            </Button>
          </div>
        </nav>

        {/* Fixed footer */}
        <div className="p-4 border-t border-slate-800 space-y-2 mt-auto">
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            onClick={() => navigate('/')}
          >
            <Home className="h-4 w-4 mr-3" />
            К конструктору
          </Button>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-3" />
            Выйти
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-8 overflow-y-auto">
        {/* Variant switcher — scopes Categories & Elements to one of the two
            sites. Stones / Model Types tabs ignore this (they are shared). */}
        <div className="mb-6 inline-flex items-center gap-1 p-1 bg-slate-200 rounded-lg" data-testid="variant-switcher">
          <button
            type="button"
            onClick={() => handleVariantChange('blagoustroistvo')}
            data-testid="variant-blagoustroistvo"
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              activeVariant === 'blagoustroistvo'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Благоустройство
          </button>
          <button
            type="button"
            onClick={() => handleVariantChange('pamyatniki')}
            data-testid="variant-pamyatniki"
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              activeVariant === 'pamyatniki'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Памятники
          </button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-6">
            {/* Search */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Поиск..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                  data-testid="search-input"
                />
              </div>
              
              {/* Category filter - only for elements tab */}
              {activeTab === 'elements' && (
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-48" data-testid="category-filter">
                    <SelectValue placeholder="Все категории" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все категории</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name_ru}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Категории элементов</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Показано {paginatedCategories.length} из {filteredCategories.length}
                  </p>
                </div>
                <Button onClick={() => openCategoryDialog()} data-testid="add-category-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Добавить категорию
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Название</TableHead>
                      <TableHead>Системное имя</TableHead>
                      <TableHead>Перемещаемый</TableHead>
                      <TableHead>Вращаемый</TableHead>
                      <TableHead>Цена установки (₸)</TableHead>
                      <TableHead>Порядок</TableHead>
                      <TableHead className="text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedCategories.map((category) => (
                      <TableRow key={category.id}>
                        <TableCell className="font-medium">{category.name_ru}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{category.name}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={category.is_draggable ? 'default' : 'secondary'}>
                            {category.is_draggable ? 'Да' : 'Нет'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={category.is_rotatable ? 'default' : 'secondary'}>
                            {category.is_rotatable ? 'Да' : 'Нет'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {category.name === 'flooring'
                            ? <span className="text-xs text-muted-foreground">У каждого элемента</span>
                            : (category.installation_price ? `${category.installation_price.toLocaleString('ru-RU')} ₸` : '—')}
                        </TableCell>
                        <TableCell>{category.order}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openCategoryDialog(category)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDeleteCategory(category.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Pagination for categories */}
                {totalCategoriesPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Страница {categoriesPage} из {totalCategoriesPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCategoriesPage(p => Math.max(1, p - 1))}
                        disabled={categoriesPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCategoriesPage(p => Math.min(totalCategoriesPages, p + 1))}
                        disabled={categoriesPage === totalCategoriesPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Elements Tab */}
          <TabsContent value="elements">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Элементы каталога</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Показано {paginatedElements.length} из {filteredElements.length}
                  </p>
                </div>
                <Button onClick={() => openElementDialog()} data-testid="add-element-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Добавить элемент
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Icon</TableHead>
                      <TableHead>Название</TableHead>
                      <TableHead>Категория</TableHead>
                      <TableHead>Размеры (Ш×Г×В)</TableHead>
                      <TableHead>Цена</TableHead>
                      <TableHead className="text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedElements.map((element) => (
                      <TableRow key={element.id}>
                        <TableCell>
                          {element.thumbnail_url ? (
                            <div className="w-8 h-8 rounded border border-slate-200 overflow-hidden bg-white">
                              <img
                                src={element.thumbnail_url.startsWith('http') ? element.thumbnail_url : element.thumbnail_url.startsWith('/api/') ? (process.env.REACT_APP_BACKEND_URL || '') + element.thumbnail_url : (process.env.PUBLIC_URL || '') + element.thumbnail_url}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div 
                              className="w-8 h-8 rounded border border-slate-200 flex items-center justify-center text-[8px] text-slate-400"
                              style={{ backgroundColor: element.color || 'transparent' }}
                            >
                              {!element.color && 'Авто'}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {element.name_ru}
                            {element.model_url && (
                              <Badge variant="secondary" className="text-[10px]">GLB</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{getCategoryName(element.category_id)}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDimensions(element.width, element.depth, element.height)}
                        </TableCell>
                        <TableCell>
                          {/* Для каменных изделий число в БД не показываем — расчёт по м³ из справочника */}
                          {isStoneBasedCategory(element.category_id) ? (
                            <span className="text-xs text-muted-foreground">
                              Из камня/м³{element.price_ratio != null && element.price_ratio !== 100 ? ` × ${element.price_ratio}%` : ''}
                            </span>
                          ) : (
                            formatPrice(element.price)
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openElementDialog(element)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDeleteElement(element.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Pagination for elements */}
                {totalElementsPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Страница {elementsPage} из {totalElementsPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setElementsPage(p => Math.max(1, p - 1))}
                        disabled={elementsPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setElementsPage(p => Math.min(totalElementsPages, p + 1))}
                        disabled={elementsPage === totalElementsPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stones Tab — справочник камней (₸/м³) для тумб, цветников, памятников */}
          <TabsContent value="stones">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Камни (цена за м³)</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Используется для расчёта цены элементов: тумба, цветник, памятник. В карточке элемента выберите камень — цена и единица (м³) подставятся автоматически.
                  </p>
                </div>
                <Button onClick={() => openStoneDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Добавить камень
                </Button>
              </CardHeader>
              <CardContent>
                {stones.length === 0 && (
                  <div className="py-8 text-center border rounded-lg bg-slate-50">
                    <p className="text-slate-600 mb-2">Камни не загружены.</p>
                    <p className="text-sm text-slate-500 mb-4">
                      Нажмите кнопку ниже, чтобы создать стандартные камни: Красный гранит, Гранит «Курты», Чёрный гранит.
                    </p>
                    <Button onClick={handleEnsureDefaultStones} disabled={ensuringStones}>
                      {ensuringStones ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Загрузить стандартные камни
                    </Button>
                  </div>
                )}
                {stones.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Название</TableHead>
                      <TableHead>Цена за м³ (₸)</TableHead>
                      <TableHead>Отображать</TableHead>
                      <TableHead>Порядок</TableHead>
                      <TableHead className="text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stones.map((stone) => (
                      <TableRow key={stone.id}>
                        <TableCell className="font-medium">{stone.name_ru}</TableCell>
                        <TableCell>{formatPrice(stone.price_per_cubic_meter)}</TableCell>
                        <TableCell>
                          <Badge variant={stone.is_active !== false ? 'default' : 'secondary'}>
                            {stone.is_active !== false ? 'Да' : 'Нет'}
                          </Badge>
                        </TableCell>
                        <TableCell>{stone.order ?? 0}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => openStoneDialog(stone)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDeleteStone(stone.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Model Types Tab */}
          <TabsContent value="model-types">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shapes className="h-5 w-5" />
                  Типы моделей
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Редактируйте отображаемые названия типов. Внутренний ключ (key) используется в коде и не меняется.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {modelTypes.map((mt) => (
                    <div key={mt.key} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-slate-50">
                      <div className="w-32">
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-600">{mt.key}</code>
                      </div>
                      {editingModelType?.key === mt.key ? (
                        <>
                          <Input
                            value={modelTypeForm.name_ru}
                            onChange={(e) => setModelTypeForm({ name_ru: e.target.value })}
                            className="flex-1"
                            autoFocus
                            onKeyDown={(e) => e.key === 'Enter' && handleSaveModelType()}
                            data-testid={`model-type-input-${mt.key}`}
                          />
                          <Button size="sm" onClick={handleSaveModelType} data-testid={`model-type-save-${mt.key}`}>
                            Сохранить
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingModelType(null)}>
                            Отмена
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-sm font-medium">{mt.name_ru}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingModelType(mt); setModelTypeForm({ name_ru: mt.name_ru }); }}
                            data-testid={`model-type-edit-${mt.key}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Category Dialog */}
      <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Редактировать категорию' : 'Новая категория'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Системное имя (англ.)</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="monument"
                />
              </div>
              <div className="space-y-2">
                <Label>Название (рус.)</Label>
                <Input
                  value={categoryForm.name_ru}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name_ru: e.target.value })}
                  placeholder="Памятник"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Описание</Label>
              <Input
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Описание категории"
              />
            </div>
            <div className="space-y-2">
              <Label>Иконка (URL или файл)</Label>
              <div className="flex gap-2 items-center flex-wrap">
                <Input
                  value={categoryForm.icon_url}
                  onChange={(e) => setCategoryForm({ ...categoryForm, icon_url: e.target.value })}
                  placeholder="https://... или загрузите файл"
                  className="flex-1 min-w-[200px]"
                />
                <input
                  ref={categoryIconInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.svg"
                  className="hidden"
                  onChange={handleCategoryIconFile}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploadingIcon}
                  onClick={() => categoryIconInputRef.current?.click()}
                >
                  {uploadingIcon ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  Файл
                </Button>
              </div>
              {categoryForm.icon_url && (
                <div className="flex items-center gap-2 mt-1">
                  <img
                    src={categoryForm.icon_url.startsWith('http') ? categoryForm.icon_url : (process.env.REACT_APP_BACKEND_URL || '') + categoryForm.icon_url}
                    alt=""
                    className="h-8 w-8 object-contain border rounded"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <span className="text-xs text-muted-foreground">Превью</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Цена установки (₸)</Label>
              <Input
                type="number"
                min="0"
                value={categoryForm.installation_price}
                onChange={(e) => setCategoryForm({ ...categoryForm, installation_price: parseFloat(e.target.value) || 0 })}
                placeholder="0 — установка не включается"
              />
              <p className="text-xs text-muted-foreground">
                Оградка и бордюр — ₸/п.м. · Покрытие и фундамент — ₸/м² · Остальные — ₸/шт
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={categoryForm.is_draggable}
                  onCheckedChange={(checked) => setCategoryForm({ ...categoryForm, is_draggable: checked })}
                />
                <Label>Перемещаемый</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={categoryForm.is_rotatable}
                  onCheckedChange={(checked) => setCategoryForm({ ...categoryForm, is_rotatable: checked })}
                />
                <Label>Вращаемый</Label>
              </div>
              <div className="space-y-2">
                <Label>Порядок</Label>
                <Input
                  type="number"
                  value={categoryForm.order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialog(false)}>
              Отмена
            </Button>
            <Button onClick={handleCategorySubmit}>
              {editingCategory ? 'Сохранить' : 'Создать'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Element Dialog */}
      <Dialog open={elementDialog} onOpenChange={setElementDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingElement ? 'Редактировать элемент' : 'Новый элемент'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Категория</Label>
              <Select
                value={elementForm.category_id}
                onValueChange={(value) => setElementForm({
                  ...elementForm,
                  category_id: value,
                  stone_id: isStoneBasedCategory(value) ? elementForm.stone_id : ''
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите категорию" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name_ru}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Системное имя</Label>
                <Input
                  value={elementForm.name}
                  onChange={(e) => setElementForm({ ...elementForm, name: e.target.value })}
                  placeholder="classic_tall"
                />
              </div>
              <div className="space-y-2">
                <Label>Название (рус.)</Label>
                <Input
                  value={elementForm.name_ru}
                  onChange={(e) => setElementForm({ ...elementForm, name_ru: e.target.value })}
                  placeholder="Стандарт малый"
                />
              </div>
            </div>
            {/* Тумба, цветник, памятник: выбор камня → цена и м³ подставляются из справочника */}
            {isStoneBasedCategory(elementForm.category_id) ? (
              <div className="space-y-2">
                <Label>Камень (цена за м³ подставится автоматически)</Label>
                <Select
                  value={elementForm.stone_id || 'none'}
                  onValueChange={(value) => {
                    const stoneId = value === 'none' ? '' : value;
                    const stone = stones.find(s => s.id === stoneId);
                    setElementForm({
                      ...elementForm,
                      stone_id: stoneId,
                      price: stone ? stone.price_per_cubic_meter : 0,
                      price_unit: stone ? 'cubic_meter' : 'piece'
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите камень" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Не выбран</SelectItem>
                    {stones.filter(s => s.is_active !== false).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name_ru} — {formatPrice(s.price_per_cubic_meter)}/м³
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {elementForm.stone_id && (
                  <p className="text-xs text-muted-foreground">
                    Расчёт: объём (Ш×Г×В) × цена за м³ выбранного камня
                  </p>
                )}
                <div className="space-y-2">
                  <Label>Процент от цены камня, %</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="200"
                      value={elementForm.price_ratio ?? 100}
                      onChange={(e) => setElementForm({ ...elementForm, price_ratio: parseFloat(e.target.value) || 100 })}
                      className="w-24"
                    />
                    <span className="text-sm text-muted-foreground">
                      {elementForm.price_ratio != null && elementForm.price_ratio !== 100
                        ? `= ${elementForm.price_ratio}% от цены камня`
                        : '100% = полная цена камня'}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Цена</Label>
                  <Input
                    type="number"
                    value={elementForm.price === '' ? '' : elementForm.price}
                    onFocus={() => {
                      if (elementForm.price === 0) setElementForm({ ...elementForm, price: '' });
                    }}
                    onChange={(e) => {
                      const v = e.target.value;
                      setElementForm({ ...elementForm, price: v === '' ? '' : (parseFloat(v) || 0) });
                    }}
                    onBlur={() => {
                      if (elementForm.price === '') setElementForm({ ...elementForm, price: 0 });
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Единица расчёта</Label>
                  <Select
                    value={elementForm.price_unit || 'piece'}
                    onValueChange={(value) => setElementForm({ ...elementForm, price_unit: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="За изделие" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="piece">За изделие (₸)</SelectItem>
                      <SelectItem value="sqmeter">За м² (ш×г) (₸/м²)</SelectItem>
                      <SelectItem value="sqmeter_wh">За м² (ш×в) (₸/м²)</SelectItem>
                      <SelectItem value="cubic_meter">За м³ (₸/м³)</SelectItem>
                      <SelectItem value="linear_meter">За п.м. (₸/п.м.)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Ширина (м)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={elementForm.width}
                  onChange={(e) => setElementForm({ ...elementForm, width: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                  onBlur={(e) => { if (e.target.value === '' || isNaN(parseFloat(e.target.value))) setElementForm(f => ({ ...f, width: 1 })); }}
                />
              </div>
              <div className="space-y-2">
                <Label>Толщина (м){getCategorySystemName(elementForm.category_id) === 'monument' ? ' — мин. 0.07 для памятников' : ''}</Label>
                <Input
                  type="number"
                  step={getCategorySystemName(elementForm.category_id) === 'monument' ? '0.01' : '0.1'}
                  min={getCategorySystemName(elementForm.category_id) === 'monument' ? '0.07' : undefined}
                  value={elementForm.depth}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setElementForm({ ...elementForm, depth: raw === '' ? '' : parseFloat(raw) });
                  }}
                  onBlur={(e) => {
                    const isMonument = getCategorySystemName(elementForm.category_id) === 'monument';
                    const fallback = isMonument ? 0.07 : 1;
                    if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                      setElementForm(f => ({ ...f, depth: fallback }));
                    }
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>Высота (м)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={elementForm.height}
                  onChange={(e) => setElementForm({ ...elementForm, height: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                  onBlur={(e) => { if (e.target.value === '' || isNaN(parseFloat(e.target.value))) setElementForm(f => ({ ...f, height: 1 })); }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Цвет (оставьте пустым для оригинального цвета модели)</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={elementForm.color || '#666666'}
                    onChange={(e) => setElementForm({ ...elementForm, color: e.target.value })}
                    className="w-12 h-10 p-1"
                  />
                  <Input
                    value={elementForm.color}
                    onChange={(e) => setElementForm({ ...elementForm, color: e.target.value })}
                    placeholder="Пусто = оригинальный цвет"
                  />
                  {elementForm.color && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setElementForm({ ...elementForm, color: '' })}
                      className="whitespace-nowrap"
                    >
                      Сбросить
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Тип модели</Label>
                <Select
                  value={elementForm.model_type}
                  onValueChange={(value) => setElementForm({ ...elementForm, model_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {modelTypes.length > 0
                      ? modelTypes.map(mt => (
                          <SelectItem key={mt.key} value={mt.key}>{mt.name_ru}</SelectItem>
                        ))
                      : (
                        <>
                          <SelectItem value="box">Параллелепипед</SelectItem>
                          <SelectItem value="cylinder">Цилиндр</SelectItem>
                          <SelectItem value="cross">Крест</SelectItem>
                          <SelectItem value="bench">Скамейка</SelectItem>
                          <SelectItem value="table">Стол</SelectItem>
                          <SelectItem value="fence">Оградка</SelectItem>
                          <SelectItem value="floor">Покрытие</SelectItem>
                          <SelectItem value="foundation">Фундамент</SelectItem>
                          <SelectItem value="pedestal">Тумба</SelectItem>
                          <SelectItem value="engraving_portrait">Гравировка портрета</SelectItem>
                          <SelectItem value="engraving_text">Гравировка ФИО</SelectItem>
                        </>
                      )
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Форма цветника: П или сплошной прямоугольник */}
            {getCategorySystemName(elementForm.category_id) === 'flowerbed' && (
              <div className="space-y-2">
                <Label>Форма цветника</Label>
                <Select
                  value={elementForm.flowerbed_shape || 'p'}
                  onValueChange={(value) => setElementForm({ ...elementForm, flowerbed_shape: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="p">Полый (бордюры)</SelectItem>
                    <SelectItem value="rectangle">Цельный</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Поля только для напольного покрытия */}
            {getCategorySystemName(elementForm.category_id) === 'flooring' && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Цена укладки (₸/м²)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={elementForm.installation_price === 0 ? '' : elementForm.installation_price}
                    placeholder="0"
                    onFocus={() => { if (elementForm.installation_price === 0) setElementForm({ ...elementForm, installation_price: '' }); }}
                    onChange={(e) => { const v = e.target.value; setElementForm({ ...elementForm, installation_price: v === '' ? '' : (parseFloat(v) || 0) }); }}
                    onBlur={() => { if (elementForm.installation_price === '') setElementForm({ ...elementForm, installation_price: 0 }); }}
                  />
                  <p className="text-xs text-muted-foreground">Стоимость работы по укладке этого покрытия. Итого = ₸/м² × площадь участка.</p>
                </div>
                <div className="flex items-center gap-3 py-1">
                  <input
                    type="checkbox"
                    id="is_flowerbed_filling"
                    checked={!!elementForm.is_flowerbed_filling}
                    onChange={(e) => setElementForm({ ...elementForm, is_flowerbed_filling: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
                  />
                  <Label htmlFor="is_flowerbed_filling" className="cursor-pointer">
                    Наполнение для цветника
                  </Label>
                </div>
              </div>
            )}

            {/* Pamyatniki: плоская доплата за фигурную форму (резка арки и т.п.).
                Хранится в поле installation_price; в цену памятника её прибавляют
                useStore.getTotalPrice / CostCalculator / Sidebar. Для blago вся ветка
                сворачивается Терсером (isPamyatniki = build-const) — бандл байт-идентичен. */}
            {isPamyatniki && getCategorySystemName(elementForm.category_id) === 'monument' && (
              <div className="space-y-2">
                <Label>Доплата за форму, ₸</Label>
                <Input
                  type="number"
                  min="0"
                  value={elementForm.installation_price === 0 ? '' : elementForm.installation_price}
                  placeholder="0"
                  onFocus={() => { if (elementForm.installation_price === 0) setElementForm({ ...elementForm, installation_price: '' }); }}
                  onChange={(e) => { const v = e.target.value; setElementForm({ ...elementForm, installation_price: v === '' ? '' : (parseFloat(v) || 0) }); }}
                  onBlur={() => { if (elementForm.installation_price === '') setElementForm({ ...elementForm, installation_price: 0 }); }}
                />
                <p className="text-xs text-muted-foreground">Фиксированная надбавка за фигурную форму (напр. резка арки у «Мусульманской»). Прибавляется к стоимости камня. 0 = без надбавки.</p>
              </div>
            )}

            {/* 3D Model Upload */}
            <div className="space-y-2">
              <Label>3D Модель (GLB/GLTF)</Label>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 items-center flex-wrap">
                  <Input
                    type="file"
                    accept=".glb,.gltf"
                    onChange={handleModelUpload}
                    disabled={uploadingModel}
                    className="flex-1 min-w-0 max-w-[260px]"
                  />
                  <Input
                    type="url"
                    placeholder="Или вставьте URL модели (GLB/GLTF)"
                    value={elementForm.model_url || ''}
                    onChange={(e) => setElementForm(prev => ({ ...prev, model_url: e.target.value.trim() }))}
                    className="flex-1 min-w-0"
                  />
                  {uploadingModel && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
                </div>
                {elementForm.model_url && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Package className="h-4 w-4" />
                    <span className="truncate max-w-xs">
                      Модель: {elementForm.model_url.split('/').pop()}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setElementForm(prev => ({ ...prev, model_url: '', model_type: 'box' }))}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Загрузите GLB/GLTF файл (макс. 50MB) или вставьте прямой URL до GLB/GLTF модели.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Иконка / превью</Label>
              <div className="flex gap-2 items-center flex-wrap">
                <Input
                  ref={elementThumbnailInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.svg"
                  onChange={handleThumbnailUpload}
                  disabled={uploadingThumbnail}
                  className="flex-1 min-w-0 max-w-[200px]"
                />
                <Input
                  type="url"
                  placeholder="Или вставьте URL"
                  value={elementForm.thumbnail_url || ''}
                  onChange={(e) => setElementForm(prev => ({ ...prev, thumbnail_url: e.target.value.trim() }))}
                  className="flex-1 min-w-0"
                />
                {uploadingThumbnail && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
              </div>
              {elementForm.thumbnail_url ? (
                <div className="flex items-center gap-2">
                  <img
                    src={elementForm.thumbnail_url.startsWith('http') ? elementForm.thumbnail_url : (process.env.REACT_APP_BACKEND_URL || '') + elementForm.thumbnail_url}
                    alt=""
                    className="w-12 h-12 rounded object-cover border border-slate-200"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setElementForm(prev => ({ ...prev, thumbnail_url: '' }))}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  PNG, JPG, WEBP или SVG (макс. 2MB). Отображается в списке в конструкторе.
                </p>
              )}
            </div>

            {/* Info for Fence/Border with GLB model */}
            {isFenceOrBorderElement() && elementForm.model_url && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-700">
                  GLB-модель оградки будет автоматически масштабирована под размер периметра.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setElementDialog(false)}>
              Отмена
            </Button>
            <Button onClick={handleElementSubmit}>
              {editingElement ? 'Сохранить' : 'Создать'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stone Dialog */}
      <Dialog open={stoneDialog} onOpenChange={setStoneDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingStone ? 'Редактировать камень' : 'Новый камень'}</DialogTitle>
            <DialogDescription>Цена за м³ используется для расчёта тумб, цветников и памятников.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Название</Label>
              <Input
                value={stoneForm.name_ru}
                onChange={(e) => setStoneForm({ ...stoneForm, name_ru: e.target.value })}
                placeholder="Напр. Красный гранит"
              />
            </div>
            <div className="space-y-2">
              <Label>Цена за м³ (₸)</Label>
              <Input
                type="number"
                value={stoneForm.price_per_cubic_meter}
                onChange={(e) => setStoneForm({ ...stoneForm, price_per_cubic_meter: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Картинка для выбора цвета</Label>
              <p className="text-xs text-muted-foreground">Отображается в блоке «Выберите цвет» в конструкторе.</p>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="w-auto max-w-[180px]"
                  onChange={handleStoneThumbnailUpload}
                  disabled={uploadingStoneThumbnail}
                />
                <Input
                  type="url"
                  placeholder="Или вставьте URL"
                  value={stoneForm.thumbnail_url || ''}
                  onChange={(e) => setStoneForm(prev => ({ ...prev, thumbnail_url: e.target.value.trim() }))}
                  className="flex-1 min-w-0"
                />
                {uploadingStoneThumbnail && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
              </div>
              <div className="flex items-center gap-2 min-h-[3rem]">
                {stoneForm.thumbnail_url ? (
                  <>
                    <img
                      src={stoneImageSrc(stoneForm.thumbnail_url) || stoneForm.thumbnail_url}
                      alt="Превью камня"
                      className="w-14 h-14 rounded object-cover border border-slate-200 bg-slate-100"
                      onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setStoneForm(prev => ({ ...prev, thumbnail_url: '' }))}
                      title="Удалить картинку"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">Картинка не загружена — загрузите файл или вставьте URL.</span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Отображать в каталоге</Label>
              <Switch
                checked={stoneForm.is_active}
                onCheckedChange={(checked) => setStoneForm({ ...stoneForm, is_active: checked })}
              />
            </div>
            <div className="space-y-2">
              <Label>Порядок сортировки</Label>
              <Input
                type="number"
                value={stoneForm.order}
                onChange={(e) => setStoneForm({ ...stoneForm, order: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStoneDialog(false)}>Отмена</Button>
            <Button onClick={handleStoneSubmit}>{editingStone ? 'Сохранить' : 'Добавить'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
